import React, { useState, useEffect, useCallback } from "react";
import AddContactModal from "../../../../Modals/AddContactModal/AddContactModal";
import AddVesselModal from "../../../../Modals/AddVesselModal/AddVesselModal";
import AddButton from "../../../../Components/Form/AddButton/AddButton";
import Svg from "../../../../Components/Svg/Svg";
import SvgOptions from "../../../../Components/Svg/SvgOptions";
import css from "./Details.module.css";
import { ModalAction } from "../../../../Routes/NavRouter";
import { Post, Delete, Get, Put } from "../../../../Services/Server";
import Url from "../../../../Services/Url";
import { toast } from "react-toastify";

// --- Vessel component remains unchanged ---
const Vessel = ({ children, onDelete, onEdit }) => {
	const { name, type } = children;
	const handleEdit = () => {
		ModalAction.openModal({
			element: AddVesselModal,
			title: "Edit Vessel",
			closeModal: (updated) => {
				if (updated) onEdit({ ...children, ...updated });
				ModalAction.closeModal();
			},
			...children,
		});
	};
	const handleDelete = () => {
		ModalAction.confirm({
			title: "Delete Vessel?",
			text: "This vessel will be permanently deleted.",
			okText: "Delete",
			exitText: "Cancel",
			onOk: () => onDelete(children),
		});
	};
	return (
		<div className={css.Vessel}>
			<div className={css.Name}>{name}</div>
			<div>{type}</div>
			<div className={css.VesselControls}>
				<div className={css.Edit}>
					<Svg onClick={handleEdit} className="Icon">
						{SvgOptions.Edit}
					</Svg>
				</div>
				<div className={css.Delete}>
					<Svg onClick={handleDelete} className="Icon">
						{SvgOptions.Delete}
					</Svg>
				</div>
			</div>
		</div>
	);
};

// --- Contact component remains unchanged and simplified as per the prompt ---
const Contact = ({ children, onDelete, onEdit }) => {
	const { name, email } = children;
	const handleEdit = () => {
		ModalAction.openModal({
			element: AddContactModal,
			title: "Edit Contact",
			closeModal: (updated) => {
				if (updated) onEdit({ ...children, ...updated });
				ModalAction.closeModal();
			},
			...children,
		});
	};
	const handleDelete = () => {
		ModalAction.confirm({
			title: "Delete Contact?",
			text: "This contact will be permanently deleted.",
			okText: "Delete",
			exitText: "Cancel",
			onOk: () => onDelete(children),
		});
	};
	return (
		<div className={css.Vessel}>
			<div className={css.Name}>{name}</div>
			<div>{email}</div>
			<div className={css.VesselControls}>
				<div className={css.Edit}>
					<Svg onClick={handleEdit} className="Icon">
						{SvgOptions.Edit}
					</Svg>
				</div>
				<div className={css.Delete}>
					<Svg onClick={handleDelete} className="Icon">
						{SvgOptions.Delete}
					</Svg>
				</div>
			</div>
		</div>
	);
};

const Right = ({ state, onChange }) => {
	const [company, setCompany] = useState(state);
	const [vessels, setVessels] = useState(state.vessels || []); // New state for vessels
	const [contacts, setContacts] = useState([]); // New state for contacts
	const [view, setView] = useState("vessels");

	// Callback to fetch contacts, memoized to prevent re-creation
	const getContacts = useCallback(() => {
		if (company?.id) {
			Get(Url.contact(company.id, "m"))
				.then((resp) => {
					setContacts(resp || []);
				})
				.catch((err) => {
					console.log(err);

					console.error("Error fetching contacts:", err);
					toast.error("Failed to load contacts.");
				});
		}
	}, [company?.id]);

	// Callback to fetch company details and vessels, memoized
	const getCompany = useCallback(() => {
		if (company?.id) {
			Get(Url.getCompany(company.id))
				.then((resp) => {
					setCompany(resp);
					setVessels(resp.vessels || []); // Update vessels state
					onChange && onChange(resp);
				})
				.catch((err) => {
					console.error("Error fetching company:", err);
					toast.error("Failed to load company details.");
				});
		}
	}, [company?.id, onChange]);

	// Initial data fetch for contacts and vessels
	useEffect(() => {
		getCompany();
		getContacts();
	}, [getCompany, getContacts]);

	// Handlers for adding, editing, and deleting vessels
	const handleAddVessel = () => {
		ModalAction.openModal({
			element: AddVesselModal,
			title: "Add Vessel",
			closeModal: (vessel) => {
				ModalAction.closeModal();
				if (vessel) {
					Post(Url.company.vessel(company.id), vessel)
						.then(() => {
							toast.success("Vessel added");
							getCompany(); // Refresh company data to get the new vessel
						})
						.catch((err) => {
							console.error(err);
							toast.error("Error adding vessel");
						});
				}
			},
			companyId: company.id,
		});
	};

	const handleVesselDelete = (vessel) => {
		Delete(Url.company.vessel(vessel.id))
			.then(() => {
				toast.success("Vessel deleted");
				getCompany(); // Refresh company data
			})
			.catch((err) => {
				console.error(err);
				toast.error("Vessel cannot be deleted");
			});
	};

	const handleVesselEdit = (vessel) => {
		Post(Url.company.vessel(company.id), vessel) // Assuming this endpoint handles both add/edit
			.then(() => {
				toast.success("Vessel updated");
				getCompany(); // Refresh company data
			})
			.catch((err) => {
				console.error(err);
				toast.error("Vessel cannot be updated");
			});
	};

	// Handlers for adding, editing, and deleting contacts
	const handleAddContact = () => {
		ModalAction.openModal({
			element: AddContactModal,
			title: "Add Contact",
			closeModal: (newContact) => {
				if (newContact) {
					Post(Url.contact(company.id, "m"), newContact)
						.then(() => {
							toast.success("Contact added");
							getContacts(); // Refresh the contact list
						})
						.catch((err) => {
							console.error(err);
							toast.error("Error adding contact");
						});
				}
				ModalAction.closeModal();
			},
			companyId: company.id,
		});
	};

	const handleContactDelete = (contact) => {
		Delete(Url.updateContact(contact.id))
			.then(() => {
				toast.success("Contact deleted");
				getContacts(); // Refresh the contact list
			})
			.catch((err) => {
				console.error(err);
				toast.error("Contact cannot be deleted");
			});
	};

	const handleContactEdit = (updatedContact) => {
		Put(Url.updateContact(updatedContact.id), updatedContact)
			.then(() => {
				toast.success("Contact updated");
				getContacts(); // Refresh the contact list
			})
			.catch((err) => {
				console.log(err);
				toast.error("Contact cannot be updated");
			});
	};

	return (
		<div className={css.Right}>
			<div className={css.tabs}>
				<button
					className={`${css.tabButton} ${view === "vessels" ? css.active : ""}`}
					onClick={() => setView("vessels")}
				>
					Vessels
				</button>
				<button
					className={`${css.tabButton} ${
						view === "contacts" ? css.active : ""
					}`}
					onClick={() => setView("contacts")}
				>
					Contacts
				</button>
			</div>

			<div className={css.sectionContainer}>
				{view === "vessels" && (
					<div>
						<div className={css.Title}>Vessels</div>
						<div className={css.Vessels}>
							{(vessels || []).map((v, i) => (
								<Vessel
									key={i}
									onDelete={handleVesselDelete}
									onEdit={handleVesselEdit}
								>
									{v}
								</Vessel>
							))}
							<AddButton onClick={handleAddVessel}>Add Vessel</AddButton>
						</div>
					</div>
				)}
				{view === "contacts" && (
					<div>
						<div className={css.Title}>Contacts</div>
						<div className={css.Vessels}>
							{(contacts || []).map((c, i) => (
								<Contact
									key={i}
									onDelete={handleContactDelete}
									onEdit={handleContactEdit}
								>
									{c}
								</Contact>
							))}
							<AddButton onClick={handleAddContact}>Add Contact</AddButton>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default Right;
